import { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import LoadingSpinner from "components/LoadingSpinner";
import VATSchemeConfigurator from "components/VATSchemeConfigurator";
import VATAdjustmentForm from "components/VATAdjustmentForm";
import HMRCOAuthConnect from "components/HMRCOAuthConnect";
import VATObligationsList from "components/VATObligationsList";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import { useAuthz } from "context/AuthzContext";
import { useClient } from "context/ClientContext";
import { listClientProfiles, apiRequest } from "services/tenantUserAdminService";

import { IoCheckmarkCircle, IoWarning, IoInformationCircle, IoRefresh, IoSettings } from "react-icons/io5";
import { TC } from "../../tokens/text";

// ─── UK VAT Box definitions ───────────────────────────────────────────────────

const VAT_BOXES = [
  { box: 1, label: "VAT due on sales and other outputs",          key: "box1", highlight: true },
  { box: 2, label: "VAT due on acquisitions from EU",            key: "box2" },
  { box: 3, label: "Total VAT due (Box 1 + Box 2)",              key: "box3", computed: true },
  { box: 4, label: "VAT reclaimed on purchases and other inputs", key: "box4", highlight: true },
  { box: 5, label: "Net VAT to pay HMRC (Box 3 – Box 4)",        key: "box5", computed: true, important: true },
  { box: 6, label: "Total value of sales (excl. VAT)",           key: "box6" },
  { box: 7, label: "Total value of purchases (excl. VAT)",       key: "box7" },
  { box: 8, label: "Total value of EU supplies of goods",        key: "box8" },
  { box: 9, label: "Total value of EU acquisitions of goods",    key: "box9" },
];

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";
const CARD_BORDER = "1px solid rgba(255,255,255,0.08)";

function fmtGBP(amount) {
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(amount || 0);
}

function StatusBadge({ status }) {
  const map = {
    draft:     { label: "Draft",     color: "#F7B731" },
    approved:  { label: "Approved",  color: "#01B574" },
    submitted: { label: "Submitted", color: "#4E72CE" },
    filed:     { label: "Filed",     color: "#A29BFE" },
  };
  const { label, color } = map[status] || map.draft;
  return (
    <span style={{ background: `${color}18`, color, border: `1px solid ${color}44`, borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>
      {label}
    </span>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VatReturn() {
  const { tenantId } = useAuthz();
  const { pinnedClientId, pinClient } = useClient() || {};

  const [profiles, setProfiles]           = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedPeriod, setSelectedPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [draft, setDraft]                 = useState(null);
  const [loading, setLoading]             = useState(false);
  const [generating, setGenerating]       = useState(false);
  const [approving, setApproving]         = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [approveOpen, setApproveOpen]     = useState(false);
  const [submitOpen, setSubmitOpen]       = useState(false);
  const [settingsOpen, setSettingsOpen]   = useState(false);
  const [hmrcOpen, setHmrcOpen]           = useState(false);
  const [obligationsOpen, setObligationsOpen] = useState(false);
  const [error, setError]                 = useState("");

  // AI state
  const [aiRunning, setAiRunning]         = useState(false);
  const [aiError, setAiError]             = useState("");
  const [aiReview, setAiReview]           = useState(null);
  const [aiFilingCheck, setAiFilingCheck] = useState(null);
  const [aiSummaryOpen, setAiSummaryOpen] = useState(false);

  // HMRC submit state
  const [hmrcSubmitting, setHmrcSubmitting] = useState(false);
  const [hmrcConnected, setHmrcConnected]   = useState(false);
  const [hmrcSubmitOpen, setHmrcSubmitOpen] = useState(false);

  const now = new Date();
  const periodOptions = Array.from({ length: 12 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return d.toISOString().slice(0, 7);
  });

  useEffect(() => {
    if (!tenantId) return;
    listClientProfiles({ tenantId, status: "active" })
      .then(d => {
        const list = d.clientProfiles || [];
        setProfiles(list);
        const nextSelected =
          (pinnedClientId && list.some((client) => client.id === pinnedClientId) && pinnedClientId) ||
          list[0]?.id ||
          "";
        setSelectedClient(nextSelected);
        if (nextSelected && nextSelected !== pinnedClientId) {
          const match = list.find((client) => client.id === nextSelected);
          pinClient?.(nextSelected, match?.legalName || match?.tradingName || nextSelected);
        }
      }).catch(() => {});
  }, [tenantId, pinnedClientId, pinClient]);

  const loadDraft = useCallback(async () => {
    if (!tenantId || !selectedClient) return;
    setLoading(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}`
      );
      setDraft(data);
    } catch (e) {
      if (e.message?.includes("404") || e.message?.toLowerCase().includes("not found")) setDraft(null);
      else setError(e.message);
    } finally { setLoading(false); }
  }, [tenantId, selectedClient, selectedPeriod]);

  useEffect(() => { loadDraft(); }, [loadDraft]);

  const handleGenerate = async () => {
    setGenerating(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/generate`,
        { method: "POST" }
      );
      setDraft(data);
    } catch (e) { setError(e.message); } finally { setGenerating(false); }
  };

  const handleApprove = async () => {
    setApproving(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/approve`,
        { method: "POST" }
      );
      setDraft(data);
      setApproveOpen(false);
    } catch (e) { setError(e.message); } finally { setApproving(false); }
  };

  const handleSubmit = async () => {
    setSubmitting(true); setError("");
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/submit`,
        { method: "POST", body: {} }
      );
      setDraft(data);
      setSubmitOpen(false);
    } catch (e) { setError(e.message); } finally { setSubmitting(false); }
  };

  // ─── AI actions ───────────────────────────────────────────────────────────────

  const handleRunAI = async () => {
    setAiRunning(true); setAiError("");
    try {
      const [review, filingCheck] = await Promise.all([
        apiRequest(
          `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/ai/review`,
          { method: "POST" }
        ),
        apiRequest(
          `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/ai/filing-check`,
          { method: "POST" }
        ),
      ]);
      setAiReview(review);
      setAiFilingCheck(filingCheck);
      // Reload draft to get updated AI fields
      loadDraft();
    } catch (e) { setAiError(e.message); } finally { setAiRunning(false); }
  };

  const handleApplyAiAdjustment = async (adj) => {
    try {
      const data = await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/vat-drafts/${selectedPeriod}/adjustments`,
        { method: "PATCH", body: { adjustment: { box: adj.box, amount: adj.amount, reason: `AI: ${adj.rationale}`, type: "ai_suggestion" } } }
      );
      setDraft(data);
    } catch (e) { setError(e.message); }
  };

  // ─── HMRC direct submit ───────────────────────────────────────────────────────

  const handleHmrcSubmit = async () => {
    setHmrcSubmitting(true); setError("");
    try {
      const data = await apiRequest(
        `/hmrc/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(selectedClient)}/returns/${selectedPeriod}/submit`,
        { method: "POST" }
      );
      setHmrcSubmitOpen(false);
      setDraft(prev => prev ? { ...prev, status: "submitted", ...data } : prev);
    } catch (e) { setError(e.message); } finally { setHmrcSubmitting(false); }
  };

  const boxes = draft?.boxes || {};
  const uncertainties = draft?.uncertainties || [];
  const adjustments   = draft?.adjustments   || [];
  const isApproved  = draft?.status === "approved";
  const isSubmitted = draft?.status === "submitted" || draft?.status === "filed";
  const isLocked    = isSubmitted;

  // Populate AI state from draft on load
  const draftAiReview    = draft?.aiReview      || aiReview;
  const draftAiFilingChk = draft?.aiFilingCheck || aiFilingCheck;
  const aiAnomalies      = draftAiReview?.anomalies || [];
  const aiSuggestions    = draftAiReview?.adjustmentSuggestions || [];
  const aiClientSummary  = draft?.aiClientSummary || null;

  const filingCheckColor = !draftAiFilingChk
    ? "rgba(255,255,255,0.3)"
    : draftAiFilingChk.ready ? "#01B574" : draftAiFilingChk.issues?.length ? "#f44336" : "#F7B731";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Header */}
        <VuiBox mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <VuiBox>
            <VuiTypography variant="h5" fontWeight="bold" sx={{ color: TC.primary }}>🧾 VAT Return</VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              AI-drafted UK VAT return — requires accountant review and approval
            </VuiTypography>
          </VuiBox>
          <VuiBox display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <Select value={selectedClient} onChange={e => {
              const next = e.target.value;
              setSelectedClient(next);
              const match = profiles.find((client) => client.id === next);
              pinClient?.(next, match?.legalName || match?.tradingName || next);
            }}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 200, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {profiles.map(p => (
                <MenuItem key={p.id} value={p.id}>{p.legalName || p.tradingName || p.id}</MenuItem>
              ))}
            </Select>
            <Select value={selectedPeriod} onChange={e => setSelectedPeriod(e.target.value)}
              sx={{ color: TC.primary, background: "#f4f8ff", border: "1px solid #d8e6f7", borderRadius: 2, minWidth: 130, "& .MuiSvgIcon-root": { color: TC.primary }, fontSize: 13 }}>
              {periodOptions.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </Select>
            <Tooltip title="VAT Settings">
              <VuiButton size="small" color="secondary" variant="outlined"
                aria-label="VAT settings"
                onClick={() => setSettingsOpen(s => !s)}
                sx={{ fontSize: 12, minWidth: 36, px: 1 }}>
                <IoSettings size={15} />
              </VuiButton>
            </Tooltip>
            <VuiButton size="small" color="info" variant="outlined"
              onClick={() => setHmrcOpen(s => !s)} sx={{ fontSize: 12 }}>
              🔗 HMRC
            </VuiButton>
            <VuiButton size="small" color="secondary" variant="outlined"
              onClick={() => setObligationsOpen(s => !s)} sx={{ fontSize: 12 }}>
              📋 Obligations
            </VuiButton>
            <VuiButton size="small" color="info" variant="outlined" startIcon={<IoRefresh size={14} />}
              onClick={loadDraft} sx={{ fontSize: 12 }}>Refresh</VuiButton>
          </VuiBox>
        </VuiBox>

        {error && (
          <VuiBox mb={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "12px 16px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}

        {/* HMRC Connection panel */}
        {hmrcOpen && selectedClient && (
          <VuiBox mb={3}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                  HMRC MTD Connection
                </VuiTypography>
                <HMRCOAuthConnect
                  tenantId={tenantId}
                  clientId={selectedClient}
                  onConnected={() => setHmrcConnected(true)}
                />
              </VuiBox>
            </Card>
          </VuiBox>
        )}

        {/* Obligations panel */}
        {obligationsOpen && selectedClient && (
          <VuiBox mb={3}>
            <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
              <VuiBox p={3}>
                <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                  VAT Obligations from HMRC
                </VuiTypography>
                <VATObligationsList
                  tenantId={tenantId}
                  clientId={selectedClient}
                  onSelectPeriod={key => { setSelectedPeriod(key); setObligationsOpen(false); }}
                />
              </VuiBox>
            </Card>
          </VuiBox>
        )}

        {/* VAT Settings panel (collapsible) */}
        {settingsOpen && selectedClient && (
          <VuiBox mb={3}>
            <VATSchemeConfigurator
              clientId={selectedClient}
              tenantId={tenantId}
              onSaved={() => setSettingsOpen(false)}
            />
          </VuiBox>
        )}

        {loading ? (
          <LoadingSpinner label="Loading…" />
        ) : !draft ? (
          <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
            <VuiBox p={4} textAlign="center">
              <VuiTypography sx={{ color: TC.secondary, fontSize: 14, mb: 2 }}>
                No VAT draft for {selectedPeriod}. Generate one from the ledger data.
              </VuiTypography>
              <VuiButton color="info" variant="contained" onClick={handleGenerate} disabled={generating || !selectedClient}>
                {generating ? "Generating…" : "Generate VAT Draft"}
              </VuiButton>
            </VuiBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {/* 9-Box Return */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
                <VuiBox p={3}>
                  <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <VuiBox>
                      <VuiTypography sx={{ color: TC.primary, fontSize: 15, fontWeight: 700 }}>
                        UK VAT Return · {selectedPeriod}
                      </VuiTypography>
                      <VuiBox mt={0.5} display="flex" alignItems="center" gap={1.5}>
                        <StatusBadge status={draft.status} />
                        {draftAiFilingChk && (
                          <span style={{ background: `${filingCheckColor}18`, color: filingCheckColor, border: `1px solid ${filingCheckColor}44`, borderRadius: 6, padding: "3px 10px", fontSize: 11, fontWeight: 700 }}>
                            {draftAiFilingChk.ready ? "✓ AI Filing Check Passed" : `⚠ Filing Check: ${draftAiFilingChk.issues?.length || 0} issue(s)`}
                          </span>
                        )}
                      </VuiBox>
                    </VuiBox>
                    <VuiBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
                      <VuiButton size="small" color="warning" variant="outlined"
                        onClick={handleRunAI} disabled={aiRunning || isLocked}
                        sx={{ fontSize: 12, fontWeight: 700 }}>
                        {aiRunning ? <><CircularProgress size={12} sx={{ color: "inherit", mr: 0.8 }} />Analysing…</> : "🤖 Run AI Analysis"}
                      </VuiButton>
                      {!isApproved && !isLocked && (
                        <VuiButton size="small" color="success" variant="contained"
                          onClick={() => setApproveOpen(true)} sx={{ fontSize: 12, fontWeight: 700 }}>
                          Approve Return ✓
                        </VuiButton>
                      )}
                      {isApproved && !isLocked && (
                        <VuiBox display="flex" alignItems="center" gap={1}>
                          <VuiBox display="flex" alignItems="center" gap={1}>
                            <IoCheckmarkCircle color="#01B574" size={18} />
                            <VuiTypography sx={{ color: "#01B574", fontSize: 13, fontWeight: 600 }}>
                              Approved {draft.approved_at ? new Date(draft.approved_at).toLocaleDateString("en-GB") : ""}
                            </VuiTypography>
                          </VuiBox>
                          <VuiButton size="small" color="info" variant="contained"
                            onClick={() => setSubmitOpen(true)} sx={{ fontSize: 12, fontWeight: 700 }}>
                            Mark as Submitted →
                          </VuiButton>
                          <VuiButton size="small" color="success" variant="contained"
                            onClick={() => setHmrcSubmitOpen(true)} sx={{ fontSize: 12, fontWeight: 700 }}>
                            Submit to HMRC ⚡
                          </VuiButton>
                        </VuiBox>
                      )}
                      {isLocked && (
                        <VuiBox display="flex" alignItems="center" gap={1}>
                          <IoCheckmarkCircle color="#4E72CE" size={18} />
                          <VuiTypography sx={{ color: "#4E72CE", fontSize: 13, fontWeight: 600 }}>
                            Submitted {draft.submitted_at ? new Date(draft.submitted_at).toLocaleDateString("en-GB") : ""}
                          </VuiTypography>
                        </VuiBox>
                      )}
                    </VuiBox>
                  </VuiBox>

                  {/* Box rows */}
                  {VAT_BOXES.map(({ box, label, key, computed, important, highlight }) => {
                    const amount = boxes[key] ?? 0;
                    const isNegative = key === "box5" && amount < 0;
                    return (
                      <VuiBox key={box} display="flex" alignItems="center" justifyContent="space-between"
                        sx={{
                          borderBottom: "1px solid #e4eef8",
                          py: "12px",
                          background: important ? "rgba(1,181,116,0.04)" : "transparent",
                          borderRadius: important ? 1 : 0,
                          px: important ? 1 : 0,
                        }}>
                        <VuiBox display="flex" alignItems="center" gap={1.5} flex={1}>
                          <span style={{
                            background: computed ? "rgba(78,205,196,0.15)" : highlight ? "rgba(99,110,226,0.15)" : "rgba(255,255,255,0.07)",
                            color: computed ? "#4ECDC4" : "rgba(255,255,255,0.5)",
                            borderRadius: 4, padding: "2px 7px",
                            fontSize: 11, fontWeight: 700, minWidth: 36, textAlign: "center",
                          }}>
                            Box {box}
                          </span>
                          <VuiTypography sx={{ color: important ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.65)", fontSize: 13, fontWeight: important ? 700 : 400 }}>
                            {label}
                          </VuiTypography>
                        </VuiBox>
                        <VuiTypography sx={{
                          color: important ? (isNegative ? "#01B574" : amount > 0 ? "#F7B731" : "rgba(255,255,255,0.6)") : "rgba(255,255,255,0.8)",
                          fontSize: important ? 16 : 14,
                          fontWeight: important ? 800 : 600,
                          minWidth: 100, textAlign: "right",
                        }}>
                          {fmtGBP(amount)}
                          {key === "box5" && amount < 0 && (
                            <span style={{ fontSize: 11, color: "#01B574", marginLeft: 4 }}>REFUND</span>
                          )}
                        </VuiTypography>
                      </VuiBox>
                    );
                  })}

                  {/* Disclaimer */}
                  <VuiBox mt={2} sx={{ background: "rgba(247,183,49,0.08)", border: "1px solid rgba(247,183,49,0.2)", borderRadius: 2, p: "10px 14px" }}>
                    <VuiTypography sx={{ color: "#F7B731", fontSize: 12 }}>
                      ⚠️ AI-generated draft — all figures require accountant review before submission to HMRC.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </Card>
            </Grid>

            {/* Sidebar: uncertainties + metadata */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", mb: 2 }}>
                <VuiBox p={3}>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                    Uncertainties & Flags
                  </VuiTypography>
                  {uncertainties.length === 0 ? (
                    <VuiBox display="flex" alignItems="center" gap={1.5} sx={{ background: "rgba(1,181,116,0.08)", border: "1px solid rgba(1,181,116,0.25)", borderRadius: 2, p: "10px 12px" }}>
                      <IoCheckmarkCircle color="#01B574" size={18} />
                      <VuiTypography sx={{ color: "#01B574", fontSize: 13 }}>No uncertainties — all VAT entries have high confidence.</VuiTypography>
                    </VuiBox>
                  ) : (
                    uncertainties.map((u, i) => (
                      <VuiBox key={i} mb={1} sx={{ background: "rgba(247,183,49,0.08)", border: "1px solid rgba(247,183,49,0.2)", borderRadius: 2, p: "8px 12px" }}>
                        <VuiBox display="flex" alignItems="center" gap={1} mb={0.3}>
                          <IoWarning color="#F7B731" size={14} />
                          <VuiTypography sx={{ color: "#F7B731", fontSize: 12, fontWeight: 600 }}>
                            {u.account || "Entry"} · {Math.round((u.confidence_score || 0) * 100)}% confidence
                          </VuiTypography>
                        </VuiBox>
                        <VuiTypography sx={{ color: TC.secondary, fontSize: 12 }}>
                          {new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(u.amount || 0)}
                        </VuiTypography>
                      </VuiBox>
                    ))
                  )}
                </VuiBox>
              </Card>

              <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px" }}>
                <VuiBox p={3}>
                  <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 2 }}>
                    Return Summary
                  </VuiTypography>
                  {[
                    { label: "Output tax (Box 1)",  value: fmtGBP(boxes.box1), color: boxes.box1 > 0 ? "#F7B731" : "rgba(255,255,255,0.6)" },
                    { label: "Input tax (Box 4)",   value: fmtGBP(boxes.box4), color: "#01B574" },
                    { label: "Net payable (Box 5)", value: fmtGBP(boxes.box5), color: (boxes.box5 || 0) < 0 ? "#01B574" : "#F7B731" },
                    { label: "Sales value (Box 6)", value: fmtGBP(boxes.box6), color: TC.midtone },
                  ].map(({ label, value, color }) => (
                    <VuiBox key={label} display="flex" justifyContent="space-between" mb={1.2}>
                      <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>{label}</VuiTypography>
                      <VuiTypography sx={{ color, fontSize: 14, fontWeight: 700 }}>{value}</VuiTypography>
                    </VuiBox>
                  ))}
                  <VuiBox mt={1.5}>
                    <VuiButton fullWidth color="info" variant="outlined" size="small"
                      onClick={handleGenerate} disabled={generating || isLocked} sx={{ fontSize: 12 }}>
                      {generating ? "Regenerating…" : "Regenerate from Ledger"}
                    </VuiButton>
                  </VuiBox>
                </VuiBox>
              </Card>

              {/* Adjustments card */}
              <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", mt: 2 }}>
                <VuiBox p={3}>
                  <VATAdjustmentForm
                    clientId={selectedClient}
                    tenantId={tenantId}
                    periodId={selectedPeriod}
                    adjustments={adjustments}
                    locked={isLocked}
                    onUpdated={setDraft}
                  />
                </VuiBox>
              </Card>

              {/* AI Error */}
              {aiError && (
                <VuiBox mt={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "10px 14px" }}>
                  <VuiTypography sx={{ color: "#f44336", fontSize: 12 }}>🤖 AI Error: {aiError}</VuiTypography>
                </VuiBox>
              )}

              {/* AI Review Card */}
              {draftAiReview && (
                <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", mt: 2 }}>
                  <VuiBox p={3}>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>
                      🤖 AI Review
                    </VuiTypography>

                    {/* Risk level */}
                    {draftAiReview.riskLevel && (
                      <VuiBox mb={1.5} display="flex" alignItems="center" gap={1}>
                        <span style={{ fontSize: 12, color: TC.secondary }}>Risk:</span>
                        <span style={{
                          background: draftAiReview.riskLevel === "low" ? "rgba(1,181,116,0.15)" : draftAiReview.riskLevel === "high" ? "rgba(255,71,87,0.15)" : "rgba(247,183,49,0.15)",
                          color: draftAiReview.riskLevel === "low" ? "#01B574" : draftAiReview.riskLevel === "high" ? "#f44336" : "#F7B731",
                          padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: "capitalize",
                        }}>
                          {draftAiReview.riskLevel}
                        </span>
                      </VuiBox>
                    )}

                    {/* Summary */}
                    {draftAiReview.summary && (
                      <VuiTypography sx={{ color: TC.midtone, fontSize: 12, mb: 1.5 }}>
                        {draftAiReview.summary}
                      </VuiTypography>
                    )}

                    {/* Anomalies */}
                    {aiAnomalies.length > 0 && (
                      <VuiBox mb={1.5}>
                        <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", mb: 0.8 }}>Anomalies</VuiTypography>
                        {aiAnomalies.map((a, i) => (
                          <VuiBox key={i} mb={0.8} sx={{ background: "rgba(247,183,49,0.07)", border: "1px solid rgba(247,183,49,0.15)", borderRadius: 2, p: "7px 10px" }}>
                            <VuiTypography sx={{ color: "#F7B731", fontSize: 11, fontWeight: 600 }}>{a.box ? `Box ${a.box}: ` : ""}{a.description || a}</VuiTypography>
                            {a.suggestion && <VuiTypography sx={{ color: TC.secondary, fontSize: 11 }}>{a.suggestion}</VuiTypography>}
                          </VuiBox>
                        ))}
                      </VuiBox>
                    )}

                    {/* AI Suggested Adjustments */}
                    {aiSuggestions.length > 0 && (
                      <VuiBox>
                        <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", mb: 0.8 }}>Suggested Adjustments</VuiTypography>
                        {aiSuggestions.map((s, i) => (
                          <VuiBox key={i} mb={1} sx={{ background: "rgba(99,110,226,0.07)", border: "1px solid rgba(99,110,226,0.2)", borderRadius: 2, p: "8px 10px" }}>
                            <VuiBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.3}>
                              <VuiTypography sx={{ color: TC.midtone, fontSize: 11, fontWeight: 600 }}>Box {s.box}: {fmtGBP(s.amount)}</VuiTypography>
                              <span style={{ background: "rgba(99,110,226,0.2)", color: "#636EE2", padding: "1px 6px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                                {Math.round((s.confidence || 0) * 100)}% conf
                              </span>
                            </VuiBox>
                            <VuiTypography sx={{ color: TC.secondary, fontSize: 11, mb: 0.8 }}>{s.rationale}</VuiTypography>
                            {!isLocked && (
                              <VuiButton size="small" color="info" variant="outlined"
                                onClick={() => handleApplyAiAdjustment(s)}
                                sx={{ fontSize: 10, py: 0.2, px: 1 }}>
                                Apply
                              </VuiButton>
                            )}
                          </VuiBox>
                        ))}
                      </VuiBox>
                    )}
                  </VuiBox>
                </Card>
              )}

              {/* AI Filing Check Card */}
              {draftAiFilingChk && (
                <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", mt: 2 }}>
                  <VuiBox p={3}>
                    <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, mb: 1.5 }}>
                      🔍 Filing Check
                    </VuiTypography>
                    <VuiBox display="flex" alignItems="center" gap={1.5} mb={draftAiFilingChk.issues?.length ? 1.5 : 0}
                      sx={{ background: `${filingCheckColor}12`, border: `1px solid ${filingCheckColor}33`, borderRadius: 2, p: "8px 12px" }}>
                      <span style={{ color: filingCheckColor, fontSize: 15 }}>{draftAiFilingChk.ready ? "✓" : "✗"}</span>
                      <VuiTypography sx={{ color: filingCheckColor, fontSize: 12, fontWeight: 600 }}>
                        {draftAiFilingChk.ready ? "Return is ready to file" : "Issues found — review before submitting"}
                      </VuiTypography>
                    </VuiBox>
                    {(draftAiFilingChk.issues || []).map((issue, i) => (
                      <VuiBox key={i} mb={0.5} sx={{ background: "rgba(255,71,87,0.07)", border: "1px solid rgba(255,71,87,0.15)", borderRadius: 2, p: "6px 10px" }}>
                        <VuiTypography sx={{ color: "#f44336", fontSize: 11 }}>{issue.description || issue}</VuiTypography>
                      </VuiBox>
                    ))}
                  </VuiBox>
                </Card>
              )}

              {/* AI Client Summary */}
              {aiClientSummary && (
                <Card sx={{ background: CARD_BG, border: CARD_BORDER, borderRadius: "16px", mt: 2 }}>
                  <VuiBox p={3}>
                    <VuiBox component="button" type="button" display="flex" justifyContent="space-between" alignItems="center" mb={1}
                      aria-expanded={aiSummaryOpen}
                      onClick={() => setAiSummaryOpen(s => !s)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          setAiSummaryOpen(s => !s);
                        }
                      }}
                      sx={{ cursor: "pointer", appearance: "none", border: 0, background: "transparent", padding: 0, width: "100%", textAlign: "left" }}>
                      <VuiTypography sx={{ color: TC.secondary, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        📝 AI Client Summary
                      </VuiTypography>
                      <span style={{ color: TC.secondary, fontSize: 12 }}>{aiSummaryOpen ? "▲" : "▼"}</span>
                    </VuiBox>
                    <Collapse in={aiSummaryOpen}>
                      <VuiTypography sx={{ color: TC.midtone, fontSize: 12, lineHeight: 1.6 }}>
                        {aiClientSummary.summary || aiClientSummary}
                      </VuiTypography>
                    </Collapse>
                  </VuiBox>
                </Card>
              )}
            </Grid>
          </Grid>
        )}

        {/* Approve Dialog */}
        <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth
          aria-labelledby="approve-vat-return-title"
          aria-describedby="approve-vat-return-description"
          PaperProps={{ sx: { background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", borderRadius: "16px", color: TC.primary } }}>
          <DialogTitle id="approve-vat-return-title" sx={{ color: TC.primary }}>Approve VAT Return?</DialogTitle>
          <DialogContent id="approve-vat-return-description">
            <VuiTypography sx={{ color: TC.midtone, fontSize: 14, mb: 1 }}>
              Net VAT payable: <strong style={{ color: "#F7B731" }}>{fmtGBP(boxes.box5)}</strong>
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              By approving, you confirm these figures are accurate and ready for HMRC submission. This also marks VAT as complete for the period close checklist.
            </VuiTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <VuiButton variant="outlined" color="white" onClick={() => setApproveOpen(false)} sx={{ fontSize: 12 }}>Cancel</VuiButton>
            <VuiButton variant="contained" color="success" onClick={handleApprove} disabled={approving} sx={{ fontSize: 12, fontWeight: 700 }}>
              {approving ? "Approving…" : "Approve ✓"}
            </VuiButton>
          </DialogActions>
        </Dialog>

        {/* Submit Dialog */}
        <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="xs" fullWidth
          aria-labelledby="submit-vat-return-title"
          aria-describedby="submit-vat-return-description"
          PaperProps={{ sx: { background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", borderRadius: "16px", color: TC.primary } }}>
          <DialogTitle id="submit-vat-return-title" sx={{ color: TC.primary }}>Mark as Submitted?</DialogTitle>
          <DialogContent id="submit-vat-return-description">
            <VuiTypography sx={{ color: TC.midtone, fontSize: 14, mb: 1 }}>
              Net VAT payable: <strong style={{ color: "#4E72CE" }}>{fmtGBP(boxes.box5)}</strong>
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              This records the return as submitted. The return will be locked and no further changes will be allowed.
              HMRC MTD direct submission is coming in a future update.
            </VuiTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <VuiButton variant="outlined" color="white" onClick={() => setSubmitOpen(false)} sx={{ fontSize: 12 }}>Cancel</VuiButton>
            <VuiButton variant="contained" color="info" onClick={handleSubmit} disabled={submitting} sx={{ fontSize: 12, fontWeight: 700 }}>
              {submitting ? "Submitting…" : "Mark as Submitted →"}
            </VuiButton>
          </DialogActions>
        </Dialog>
        {/* HMRC Submit Dialog */}
        <Dialog open={hmrcSubmitOpen} onClose={() => setHmrcSubmitOpen(false)} maxWidth="xs" fullWidth
          aria-labelledby="hmrc-submit-vat-return-title"
          aria-describedby="hmrc-submit-vat-return-description"
          PaperProps={{ sx: { background: "rgba(15,18,59,0.98)", border: "1px solid #d8e6f7", borderRadius: "16px", color: TC.primary } }}>
          <DialogTitle id="hmrc-submit-vat-return-title" sx={{ color: TC.primary }}>Submit to HMRC MTD?</DialogTitle>
          <DialogContent id="hmrc-submit-vat-return-description">
            <VuiTypography sx={{ color: TC.midtone, fontSize: 14, mb: 1 }}>
              Net VAT payable: <strong style={{ color: "#01B574" }}>{fmtGBP(boxes.box5)}</strong>
            </VuiTypography>
            <VuiTypography sx={{ color: TC.secondary, fontSize: 13 }}>
              This will submit the approved return directly to HMRC via Making Tax Digital. Ensure you are connected to HMRC before proceeding.
            </VuiTypography>
            {draftAiFilingChk && !draftAiFilingChk.ready && (
              <VuiBox mt={1.5} sx={{ background: "rgba(255,71,87,0.08)", border: "1px solid rgba(255,71,87,0.25)", borderRadius: 2, p: "8px 12px" }}>
                <VuiTypography sx={{ color: "#f44336", fontSize: 12 }}>
                  ⚠️ AI Filing Check flagged issues — please review before submitting.
                </VuiTypography>
              </VuiBox>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <VuiButton variant="outlined" color="white" onClick={() => setHmrcSubmitOpen(false)} sx={{ fontSize: 12 }}>Cancel</VuiButton>
            <VuiButton variant="contained" color="success" onClick={handleHmrcSubmit} disabled={hmrcSubmitting} sx={{ fontSize: 12, fontWeight: 700 }}>
              {hmrcSubmitting ? "Submitting to HMRC…" : "Submit to HMRC ⚡"}
            </VuiButton>
          </DialogActions>
        </Dialog>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

